/**
 * Main application layout with sidebar
 */
import {
    Help as HelpIcon,
    Logout,
    Menu as MenuIcon,
    Person,
    Settings,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { Sidebar, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './Sidebar';
import { useInstructionsGuideStore } from '@/store/instructionsGuideStore';

interface MainLayoutProps {
  children: React.ReactNode;
  onSidebarStateChange?: (collapsed: boolean) => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, onSidebarStateChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const openGuide = useInstructionsGuideStore((s) => s.openGuide);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/auth');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    const newCollapsed = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsed);
    onSidebarStateChange?.(newCollapsed);
  };

  const handleLogoClick = () => {
    navigate('/app/home');
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0).toUpperCase() || '';
    const last = lastName?.charAt(0).toUpperCase() || '';
    return first + last || user?.email?.charAt(0).toUpperCase() || '?';
  };

  const isAdmin = user && (user.role === UserRole.ADMIN || user.role === 'admin');

  return (
    <>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:hover': {
                  color: 'primary.dark',
                }
              }}
              onClick={handleLogoClick}
            >
              QuickField
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  '& .help-icon': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  }
                }
              }}
              onClick={() => openGuide()}
            >
              <IconButton
                size="small"
                className="help-icon"
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  }
                }}
                title="User Guide"
              >
                <HelpIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Help center
              </Typography>
            </Box>
          </Box>

          {/* User menu */}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Admin button - for admin and organization admin users */}
              {(isAdmin || (user.role === UserRole.ORGANIZATION_ADMIN || user.role === 'organization_admin')) && (
                <IconButton
                  size="large"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Settings button clicked');
                    console.log('User role:', user.role);
                    console.log('isAdmin:', isAdmin);
                    console.log('isOrgAdmin:', user.role === UserRole.ORGANIZATION_ADMIN || user.role === 'organization_admin');

                    // Navigate to appropriate admin panel based on role
                    if (isAdmin) {
                      console.log('Navigating to /admin');
                      navigate('/admin', { replace: false });
                    } else if (user.role === UserRole.ORGANIZATION_ADMIN || user.role === 'organization_admin') {
                      console.log('Navigating to /organization-admin');
                      navigate('/organization-admin', { replace: false });
                    } else {
                      console.log('Unknown role, navigating to /app/history');
                      navigate('/app/history', { replace: false });
                    }
                  }}
                  color="inherit"
                  title={isAdmin ? "Admin Panel" : "Organization Admin Panel"}
                  aria-label={isAdmin ? "Admin Panel" : "Organization Admin Panel"}
                  sx={{
                    mr: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <Settings />
                </IconButton>
              )}

              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                {user.full_name || user.email}
              </Typography>

              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {getInitials(user.first_name, user.last_name)}
                </Avatar>
              </IconButton>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 200 }
                }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH },
          flexShrink: { md: 0 },
          transition: 'width 0.3s ease',
          mt: 8, // Account for AppBar height
        }}
      >
        {/* Mobile drawer */}
        {isMobile ? (
          <Sidebar
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
          />
        ) : (
          <Sidebar
            variant="permanent"
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
          />
        )}
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: {
            md: `${SIDEBAR_WIDTH - 180}px`
          },
          mt: 8, // Account for AppBar height
          transition: 'margin 0.3s ease',
          height: 'calc(100vh - 64px)', // Full height minus AppBar
          overflow: 'auto', // Enable scrolling for main content
          minWidth: 0, // Allow content to shrink
          boxSizing: 'border-box',
        }}
      >
        {React.isValidElement(children)
          ? React.cloneElement(children, { mainSidebarCollapsed: sidebarCollapsed })
          : children}
      </Box>
    </Box>
    </>
  );
};
